import { z } from 'zod';
import { updateCurrentUser } from '@mews-commander/core';
import {
    UpdateAuthenticatorParameters,
    UpdateAuthenticatorResult,
    useRepositoryMutation,
} from '@mews-commander/data-access';
import { createZodFormResolver, FormInputField, LocalizedText, useFormControls } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';

interface AuthenticatorCodeFormValues {
    pin: string;
}

const resolver = createZodFormResolver<AuthenticatorCodeFormValues>(
    z.object({
        pin: z.string(),
    })
);

export const useAuthenticatorCodeForm = ({
    id,
    params,
    onSuccess,
}: {
    id?: string;
    params: Omit<UpdateAuthenticatorParameters, 'pin'>;
    onSuccess?: (data: UpdateAuthenticatorResult) => void;
}) => {
    const { mutate, isPending } = useRepositoryMutation('users', 'updateAuthenticator', {
        onSuccess: (_, { authenticator }) => {
            updateCurrentUser({ authenticator });
        },
        meta: {
            disableDefaultErrorHandler: true,
        },
    });

    const { renderFormContent, submit, setError } = useFormControls<AuthenticatorCodeFormValues>({
        id,
        onSubmit: (values) =>
            mutate(
                {
                    ...params,
                    // We remove spaces from the pin to ensure a better UX
                    pin: values.pin.replace(/\s/g, ''),
                },
                { onSuccess, onError: (e) => setError('pin', { message: e instanceof Error ? e.message : '' }) }
            ),
        resolver,
        defaultValues: {
            pin: '',
        },
    });

    return { renderFormContent, submit, isLoading: isPending };
};

export const AuthenticatorCodeField = () => (
    <FormInputField name="pin" required label={<LocalizedText textKey={TextKey.AuthenticationCode} />} />
);
